import type { EdgeRole, NodeRef, RepertoireLine, RepertoireNode } from '../api/types';
import { sameNode } from './navigation';

export interface StoredDraft {
  version: 1;
  line: RepertoireLine;
  name: string;
  building: boolean;
  role: EdgeRole;
}

const key = (repertoireId: string) => `chessreps.draft.v1.${repertoireId}`;

export function readDraft(repertoireId: string): StoredDraft | null {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(key(repertoireId)) ?? 'null');
    const line = value?.line;
    if (value?.version !== 1 || line?.repertoireId !== repertoireId || line.lineId !== 'draft' ||
      typeof line.rootId !== 'string' || !Array.isArray(line.movesUci) ||
      !line.movesUci.every((move: unknown) => typeof move === 'string' && /^[a-h][1-8][a-h][1-8][qrbn]?$/.test(move)) ||
      !Array.isArray(line.nodes) || line.nodes.length !== line.movesUci.length + 1 ||
      !line.nodes.every((node: NodeRef) => node && typeof node.hashVersion === 'number' &&
        typeof node.zobristHash === 'string' && typeof node.positionId === 'string') ||
      !Array.isArray(line.movesSan) || line.movesSan.length !== line.movesUci.length ||
      !line.movesSan.every((san: unknown) => typeof san === 'string') ||
      typeof value.name !== 'string' || typeof value.building !== 'boolean' ||
      !['main', 'alternative', 'opponent', 'avoid'].includes(value.role)) return null;
    return value as StoredDraft;
  } catch {
    return null;
  }
}

/** Tab-scoped storage preserves refreshes without mixing drafts between tabs. */
export function writeDraft(repertoireId: string, value: StoredDraft | null): boolean {
  try {
    if (value) window.sessionStorage.setItem(key(repertoireId), JSON.stringify(value));
    else window.sessionStorage.removeItem(key(repertoireId));
    return true;
  } catch {
    return false;
  }
}

/** Find a saved route for review, never claim it is the lost original move order. */
export async function recoverDraft(
  repertoireId: string,
  rootId: string,
  root: NodeRef,
  target: NodeRef,
  getNode: (ref: NodeRef) => Promise<RepertoireNode>,
  limit = 200,
): Promise<RepertoireLine | null> {
  const initial: RepertoireLine = {
    repertoireId, rootId, lineId: 'draft', name: 'Recovered variation', active: true,
    createdAt: new Date().toISOString(), nodes: [root], movesUci: [], movesSan: [], edgeIds: [],
  };
  const queue = [initial];
  const visited = new Set<string>();
  for (let i = 0; i < queue.length && i < limit; i++) {
    const line = queue[i];
    const ref = line.nodes[line.nodes.length - 1];
    if (sameNode(ref, target)) return line;
    const node = await getNode(ref);
    for (const edge of node.outgoing) {
      const next = edge.transition.to;
      const identity = `${next.hashVersion}/${next.zobristHash}/${next.positionId}`;
      if (visited.has(identity)) continue;
      visited.add(identity);
      const candidate = {
        ...line,
        nodes: [...line.nodes, next],
        movesUci: [...line.movesUci, edge.transition.uci],
        movesSan: [...(line.movesSan ?? []), edge.transition.san],
        edgeIds: [...line.edgeIds, edge.edgeId],
      };
      if (sameNode(next, target)) return candidate;
      queue.push(candidate);
    }
  }
  return null;
}
